<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SectionLayout from '../account/components/SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { accountId } = useAccount();

// Legacy family token: covers all WhatsApp numbers. On load it lights up the
// granular per-number boxes; on save we write granular tokens only.
const WHATSAPP_FAMILY = 'whatsapp';
const WHATSAPP_GRANULAR = ['whatsapp3721', 'whatsapp5388', 'whatsapp6855'];

// Dynamic channels (owner 2026-06-30): the toggle list is built from the live
// Chatwoot inboxes, so a newly-added channel appears automatically. The known
// marketplace / WhatsApp inboxes keep their legacy semantic tokens (mirrored in
// mk-agent's Parse & Filter CHANNEL_MAP + the backend allowlist); every other
// inbox uses a stable per-inbox key `inbox<id>`. Keep this map in lockstep with
// mk-agent's CHANNEL_MAP.
const KNOWN_TOKEN = {
  1: 'whatsapp3721',
  2: 'shopee',
  52: 'whatsapp5388',
  53: 'tiktok',
  55: 'lazada',
  56: 'ntest',
  59: 'whatsapp6855',
  60: 'email',
};
const tokenForInbox = inbox => KNOWN_TOKEN[inbox.id] || `inbox${inbox.id}`;

const store = useStore();
const inboxes = useMapGetter('inboxes/getInboxes');

// One checkbox per current inbox, shown by name, in a stable order: known
// channels first (canonical order), then any new inboxes by token.
const KNOWN_ORDER = [
  'shopee',
  'tiktok',
  'lazada',
  'whatsapp3721',
  'whatsapp5388',
  'whatsapp6855',
  'ntest',
  'email',
];
const channelOptions = computed(() =>
  [...(inboxes.value || [])]
    .map(inbox => ({ token: tokenForInbox(inbox), label: inbox.name }))
    .sort((a, b) => {
      const ia = KNOWN_ORDER.indexOf(a.token);
      const ib = KNOWN_ORDER.indexOf(b.token);
      const ra = ia === -1 ? KNOWN_ORDER.length : ia;
      const rb = ib === -1 ? KNOWN_ORDER.length : ib;
      return ra === rb ? a.token.localeCompare(b.token) : ra - rb;
    })
);

// AUTO-SEND channel list — EXCLUDES email. Email is DRAFT-ONLY: mk-agent's
// intake (Parse & Filter CHANNEL_MAP) has no inbox-60 entry, so an email
// message parses to the per-inbox key `inbox60`, never the 'email' token. The
// auto-send `channels` allowlist is matched on that intake key, so an 'email'
// entry there could never fire — an auto-send checkbox for email would be a
// silent no-op. So the AUTO-SEND checkboxes use this filtered list (no email),
// while the AI-Draft-per-channel toggles below still use `channelOptions`
// (email included, canonicalized to the 'email' token via KNOWN_TOKEN[60]).
const autoSendChannelOptions = computed(() =>
  channelOptions.value.filter(o => o.token !== 'email')
);

// ISO weekday order Mon=1..Sun=7, for the working-hours day pills.
const WEEK_DAYS = [
  { value: 1, key: 'MON' },
  { value: 2, key: 'TUE' },
  { value: 3, key: 'WED' },
  { value: 4, key: 'THU' },
  { value: 5, key: 'FRI' },
  { value: 6, key: 'SAT' },
  { value: 7, key: 'SUN' },
];

const MODE = { OFF: 'off', SCHEDULED: 'scheduled', ALWAYS: 'always' };

// Behaviour-class tokens (mirror the controller allowlists + mk-agent
// day-safe-classes.js / nightPromoteArmed). Order = display order.
const DAY_SAFE_OPTIONS = ['positive_close', 'store_facts', 'grounded_stock'];
const NIGHT_PROMOTE_OPTIONS = ['verified_defer', 'verified_defer_order'];

// needs_human_ack is three-state, not a checkbox: `dark` authors the acknowledgement and
// sends none of them, which is the only way to read the AI's wording before a customer does.
const NEEDS_HUMAN_ACK_OPTIONS = ['off', 'dark', 'on'];
const NEEDS_HUMAN_ACK_COOLDOWN_DEFAULT = 90;
const NEEDS_HUMAN_ACK_COOLDOWN_MIN = 15;
const NEEDS_HUMAN_ACK_COOLDOWN_MAX = 720;

const isLoading = ref(true);
const isSaving = ref(false);
const rawConfig = ref({});

// bot-hours state
const mode = ref(MODE.OFF);
const nightStart = ref('18:00');
const nightEnd = ref('09:00');
const selectedChannels = ref([]);

// AI-draft per channel (owner 2026-07-01): the tokens where AI drafting is turned OFF — mk-agent skips those
// inboxes entirely at its pre-filter (no draft, no reply). Blocklist: a channel not listed drafts as normal, so
// this is independent of the auto-send Mode above. Stored as cs:autopilot:config.draft_off_channels.
const draftOffChannels = ref([]);

// working-hours (team) state
const workDays = ref([1, 2, 3, 4, 5, 6]);
const workStart = ref('10:00');
const workEnd = ref('18:00');

// instant-delivery staff alerts (separate toggles, follow working hours above)
const instantOrderEnabled = ref(true);
const instantSummaryEnabled = ref(true);

// daily "Summary to Admin" — night auto-reply quality digest to the owner
const adminSummaryEnabled = ref(true);

// independent third-party review before send — an AI CS-reviewer must approve each outgoing draft
// (read by mk-agent run.js gateDraft). Default ON; the deterministic safety gates run regardless.
const reviewerEnabled = ref(true);

// Formerly-hidden behaviour dials (settings-truthfulness 2026-07-12). All read
// live per-turn by mk-agent from cs:autopilot:config. An absent key currently
// means OFF / empty, so we surface exactly that — the page tells the truth.
// night_ack: night "a colleague will follow up next business day" note (+ planner
// ack text) on held/escalated night turns. verify_nudge: a 2nd verify pass before
// a SCHEDULED/Always auto-send. ack_sticker: positive-close send → native sticker
// on WhatsApp/Shopee AND the Shopee CRR closer sticker. ack_sticker_id: which
// sticker (pack:id). day_safe_classes: which confident classes auto-send by DAY.
// night_safe_promote: which verified night defers may be promoted to a send.
const nightAck = ref(false);
const verifyNudge = ref(false);
const ackSticker = ref(false);
const ackStickerId = ref('');
const daySafeClasses = ref([]);
const nightSafePromote = ref([]);

// keep_unreplied_enabled: master for the whole mh_keep_unreplied mechanism — the AI's own
// election, the bridge's mechanical floor, and the interlock that lets the held-turn ack send
// at all. It has been a redis-only flag since 2026-07-22 with no control anywhere; surfacing it
// is what makes the mechanism settings-truthful instead of hidden. Absent key = OFF.
// needs_human_ack / needs_human_ack_cooldown_min: the held-turn acknowledgement lane and its
// per-conversation burst floor (owner 2026-07-29). Absent = 'off' / mk-agent's 90-minute default.
const keepUnrepliedEnabled = ref(false);
const needsHumanAck = ref('off');
const needsHumanAckCooldownMin = ref(NEEDS_HUMAN_ACK_COOLDOWN_DEFAULT);

// AI exclude list — accounts (phone / email / username) the AI must NEVER reply to (internal staff, test numbers).
const excludeAccounts = ref([]);
const newExclude = ref('');

// AI reply wait — sliding debounce window (seconds). The AI waits this long after the customer's LAST
// message before drafting, so a request typed across several messages becomes ONE reply (each new message
// restarts the countdown; the reply box shows it ticking). 0 = reply immediately. Read by mk-agent (defer.js).
const debounceSeconds = ref(120);

// live clock for the status chip (MYT, UTC+8 fixed)
const nowTick = ref(Date.now());
let clockTimer = null;

const baseUrl = computed(
  () => `/api/v1/accounts/${accountId.value}/mh_autopilot`
);

// Same 15..720 bound the controller enforces, applied on both hydrate and save so the
// number box can never post a value the API would 422 on.
const clampCooldown = value =>
  Math.max(
    NEEDS_HUMAN_ACK_COOLDOWN_MIN,
    Math.min(NEEDS_HUMAN_ACK_COOLDOWN_MAX, Math.round(value))
  );

// ---- mode derivation -------------------------------------------------------
const isAlwaysWindow = (start, end) => start === '00:00' && end === '24:00';

const deriveMode = config => {
  if (String(config.night_enabled) !== '1') return MODE.OFF;
  if (isAlwaysWindow(config.night_start, config.night_end)) return MODE.ALWAYS;
  return MODE.SCHEDULED;
};

const hydrate = config => {
  rawConfig.value = config || {};
  mode.value = deriveMode(config);

  // Only pull times into the pickers when they describe a real schedule;
  // 24/7 stores 00:00/24:00 which we don't want to leak into the inputs.
  if (mode.value === MODE.SCHEDULED) {
    nightStart.value = config.night_start || '18:00';
    nightEnd.value = config.night_end || '09:00';
  }

  // Selected tokens come straight from the saved csv (independent of whether the
  // inbox list has loaded yet, so a save never drops a token for a not-yet-shown
  // inbox). Legacy `whatsapp` family token → the granular per-number tokens.
  const csv = (config.channels || '')
    .split(',')
    .map(c => c.trim().toLowerCase())
    .filter(Boolean);
  const expanded = csv.flatMap(c =>
    c === WHATSAPP_FAMILY ? WHATSAPP_GRANULAR : [c]
  );
  selectedChannels.value = [...new Set(expanded)];

  // AI-draft per channel — the saved OFF tokens (blocklist). Absent → nothing off → every channel drafts.
  draftOffChannels.value = (config.draft_off_channels || '')
    .split(',')
    .map(c => c.trim().toLowerCase())
    .filter(Boolean);

  const days = (config.work_days || '')
    .split(',')
    .map(d => Number(d.trim()))
    .filter(d => d >= 1 && d <= 7);
  if (days.length) workDays.value = days;
  workStart.value = config.work_start || '10:00';
  workEnd.value = config.work_end || '18:00';

  // Instant-delivery toggles default ON when the key is absent (pre-rollout).
  instantOrderEnabled.value =
    String(config.instant_order_enabled ?? '1') !== '0';
  instantSummaryEnabled.value =
    String(config.instant_summary_enabled ?? '1') !== '0';
  adminSummaryEnabled.value =
    String(config.admin_summary_enabled ?? '1') !== '0';

  // third-party review default ON when absent (pre-rollout); only an explicit "0" disables it.
  reviewerEnabled.value = String(config.reviewer_enabled ?? '1') !== '0';

  // Formerly-hidden behaviour dials — absent key = OFF (toggles) / empty (lists).
  nightAck.value = String(config.night_ack) === '1';
  verifyNudge.value = String(config.verify_nudge) === '1';
  ackSticker.value = String(config.ack_sticker) === '1';
  ackStickerId.value = config.ack_sticker_id || '';
  daySafeClasses.value = (config.day_safe_classes || '')
    .split(',')
    .map(c => c.trim().toLowerCase())
    .filter(Boolean);
  nightSafePromote.value = (config.night_safe_promote || '')
    .split(',')
    .map(c => c.trim().toLowerCase())
    .filter(Boolean);

  // Held-turn acknowledgement lane — absent keys mean OFF / the 90-minute default, and an
  // unrecognised value reads as OFF so a hand-edited redis string can never arm a send.
  keepUnrepliedEnabled.value = String(config.keep_unreplied_enabled) === '1';
  const ackState = String(config.needs_human_ack || '')
    .trim()
    .toLowerCase();
  needsHumanAck.value = NEEDS_HUMAN_ACK_OPTIONS.includes(ackState)
    ? ackState
    : 'off';
  const cooldown = Number(config.needs_human_ack_cooldown_min);
  needsHumanAckCooldownMin.value =
    config.needs_human_ack_cooldown_min != null &&
    config.needs_human_ack_cooldown_min !== '' &&
    Number.isFinite(cooldown)
      ? clampCooldown(cooldown)
      : NEEDS_HUMAN_ACK_COOLDOWN_DEFAULT;

  excludeAccounts.value = (config.exclude_accounts || '')
    .split(',')
    .map(x => x.trim())
    .filter(Boolean);

  // AI reply wait (seconds). Absent → 120s default; clamp to the 0..600 the backend accepts.
  const ds = Number(config.debounce_seconds);
  debounceSeconds.value =
    config.debounce_seconds != null &&
    config.debounce_seconds !== '' &&
    Number.isFinite(ds)
      ? Math.max(0, Math.min(600, ds))
      : 120;
};

// ---- live status chip (computed in MYT) ------------------------------------
const mytNow = computed(() => {
  // shift the current instant into UTC+8 and read its clock fields
  const d = new Date(nowTick.value + 8 * 60 * 60 * 1000);
  // getUTCDay: 0=Sun..6=Sat → ISO 1=Mon..7=Sun
  const isoDay = d.getUTCDay() === 0 ? 7 : d.getUTCDay();
  return { minutes: d.getUTCHours() * 60 + d.getUTCMinutes(), isoDay };
});

const toMinutes = hhmm => {
  const [h, m] = (hhmm || '0:0').split(':').map(Number);
  return h * 60 + m;
};

// Is `now` inside [start, end), supporting overnight wrap (end <= start).
const withinWindow = (nowMin, start, end) => {
  const s = toMinutes(start);
  const e = toMinutes(end);
  if (s === e) return false;
  if (s < e) return nowMin >= s && nowMin < e;
  return nowMin >= s || nowMin < e; // overnight wrap
};

// A non-work day (e.g. Sunday/holiday) is full-day off-hours → the whole day counts
// as night, so the AI auto-sends round the clock on off-days (mk-agent arming parity).
const isOffDayNow = computed(
  () => !workDays.value.includes(mytNow.value.isoDay)
);

const isBotActiveNow = computed(() => {
  if (mode.value === MODE.OFF) return false;
  if (mode.value === MODE.ALWAYS) return true;
  // Mode SCHEDULED: armed inside the night window OR all day on a non-work day.
  return (
    isOffDayNow.value ||
    withinWindow(mytNow.value.minutes, nightStart.value, nightEnd.value)
  );
});

// Team is working only on a selected weekday AND inside the work window.
const isTeamWorkingNow = computed(() => {
  if (!workDays.value.includes(mytNow.value.isoDay)) return false;
  return withinWindow(mytNow.value.minutes, workStart.value, workEnd.value);
});

// A send needs a selected auto-send channel that is NOT AI-Draft-off: mk-agent drops the
// event entirely (before every normal/day-safe/CRR path) when the channel is in
// draft_off_channels (server.js:257/262). So a channel that is selected but draft-off sends nothing.
const hasSendChannel = computed(() =>
  selectedChannels.value.some(c => !draftOffChannels.value.includes(c))
);

// day_safe classes auto-send ONLY when the schedule did NOT arm this moment
// (mk-agent day-safe-classes.js).
const daySafeActiveNow = computed(
  () =>
    mode.value !== MODE.OFF &&
    !isBotActiveNow.value &&
    daySafeClasses.value.length > 0
);

// the Shopee CRR closer can fire at ANY hour when ack_sticker is on and Mode isn't Off
// (mk-agent server.js:692) — but only while Shopee is not AI-Draft-off (draft_off drops the
// event before the CRR path).
const crrActiveNow = computed(
  () =>
    mode.value !== MODE.OFF &&
    ackSticker.value &&
    !draftOffChannels.value.includes('shopee')
);

// one truthful state both indicators read from: 'off' | 'full' | 'limited' | 'draft'.
const sendState = computed(() => {
  if (mode.value === MODE.OFF) return 'off';
  if (isBotActiveNow.value && hasSendChannel.value) return 'full';
  if ((daySafeActiveNow.value && hasSendChannel.value) || crrActiveNow.value) {
    return 'limited';
  }
  return 'draft';
});

const statusChip = computed(() => {
  switch (sendState.value) {
    case 'off':
      return { tone: 'off', label: t('AI_AUTOPILOT.STATUS.OFF') };
    case 'full': {
      const team = isTeamWorkingNow.value
        ? t('AI_AUTOPILOT.STATUS.TEAM_WORKING')
        : t('AI_AUTOPILOT.STATUS.TEAM_OFF');
      return {
        tone: 'on',
        label: `${t('AI_AUTOPILOT.STATUS.ACTIVE')} · ${team}`,
      };
    }
    case 'limited':
      return { tone: 'on', label: t('AI_AUTOPILOT.STATUS.LIMITED') };
    default:
      return { tone: 'sleeping', label: t('AI_AUTOPILOT.STATUS.DRAFT') };
  }
});

// ---- effective-status banner (what the AI is doing RIGHT NOW) --------------
// Reads the same single sendState the top chip does, so the two can never
// disagree. 'full' => the schedule/24-7 armed a real auto-send; 'limited' =>
// only day-safe classes and/or the Shopee CRR closer sticker can send right now;
// 'draft' => daytime on a work day with neither armed. MYT = UTC+8 (mytNow).
const effectiveStatus = computed(() => {
  switch (sendState.value) {
    case 'off':
      return { tone: 'off', label: t('AI_AUTOPILOT.EFFECTIVE.OFF') };
    case 'full': {
      let reason;
      if (mode.value === MODE.ALWAYS) {
        reason = t('AI_AUTOPILOT.EFFECTIVE.REASON_ALWAYS');
      } else if (
        withinWindow(mytNow.value.minutes, nightStart.value, nightEnd.value)
      ) {
        reason = t('AI_AUTOPILOT.EFFECTIVE.REASON_NIGHT');
      } else {
        reason = t('AI_AUTOPILOT.EFFECTIVE.REASON_OFFDAY');
      }
      return {
        tone: 'on',
        label: t('AI_AUTOPILOT.EFFECTIVE.SENDING', { reason }),
      };
    }
    case 'limited': {
      const what = [];
      if (daySafeActiveNow.value && hasSendChannel.value) {
        what.push(
          t('AI_AUTOPILOT.EFFECTIVE.LIMITED_DAYSAFE', {
            count: daySafeClasses.value.length,
          })
        );
      }
      if (crrActiveNow.value) {
        what.push(t('AI_AUTOPILOT.EFFECTIVE.LIMITED_CRR'));
      }
      return {
        tone: 'on',
        label: t('AI_AUTOPILOT.EFFECTIVE.LIMITED', { what: what.join(' + ') }),
      };
    }
    default:
      return { tone: 'draft', label: t('AI_AUTOPILOT.EFFECTIVE.DRAFT_ONLY') };
  }
});

// ---- channels --------------------------------------------------------------
const toggleChannel = channel => {
  selectedChannels.value = selectedChannels.value.includes(channel)
    ? selectedChannels.value.filter(c => c !== channel)
    : [...selectedChannels.value, channel];
};

// AI-draft per channel: a channel is "AI on" unless its token is in the OFF list. Toggling flips membership.
const isDraftOn = token => !draftOffChannels.value.includes(token);
const toggleDraftChannel = token => {
  draftOffChannels.value = draftOffChannels.value.includes(token)
    ? draftOffChannels.value.filter(c => c !== token)
    : [...draftOffChannels.value, token];
};

// ---- work days -------------------------------------------------------------
const toggleWorkDay = day => {
  workDays.value = workDays.value.includes(day)
    ? workDays.value.filter(d => d !== day)
    : [...workDays.value, day].sort((a, b) => a - b);
};

// ---- behaviour-class multi-selects -----------------------------------------
const toggleDaySafe = token => {
  daySafeClasses.value = daySafeClasses.value.includes(token)
    ? daySafeClasses.value.filter(c => c !== token)
    : [...daySafeClasses.value, token];
};
const toggleNightPromote = token => {
  nightSafePromote.value = nightSafePromote.value.includes(token)
    ? nightSafePromote.value.filter(c => c !== token)
    : [...nightSafePromote.value, token];
};

// ---- exclude list ----------------------------------------------------------
const addExclude = () => {
  const v = newExclude.value.trim();
  if (!v) return;
  const exists = excludeAccounts.value.some(
    x => x.toLowerCase() === v.toLowerCase()
  );
  if (!exists) excludeAccounts.value = [...excludeAccounts.value, v];
  newExclude.value = '';
};
const removeExclude = item => {
  excludeAccounts.value = excludeAccounts.value.filter(x => x !== item);
};

// ---- persistence -----------------------------------------------------------
const buildPayload = () => {
  const payload = {};

  if (mode.value === MODE.OFF) {
    payload.night_enabled = '0';
  } else if (mode.value === MODE.ALWAYS) {
    payload.night_enabled = '1';
    payload.night_start = '00:00';
    payload.night_end = '24:00';
  } else {
    payload.night_enabled = '1';
    payload.night_start = nightStart.value;
    payload.night_end = nightEnd.value;
  }

  // Granular tokens only (the legacy `whatsapp` family token is never written back).
  payload.channels = [...new Set(selectedChannels.value)].join(',');

  // Per-channel AI-draft kill switch — only the OFF tokens are stored (blocklist); empty = draft everywhere.
  payload.draft_off_channels = [...new Set(draftOffChannels.value)].join(',');

  // working hours (team)
  payload.work_days = [...workDays.value].sort((a, b) => a - b).join(',');
  payload.work_start = workStart.value;
  payload.work_end = workEnd.value;

  // instant-delivery staff alerts
  payload.instant_order_enabled = instantOrderEnabled.value ? '1' : '0';
  payload.instant_summary_enabled = instantSummaryEnabled.value ? '1' : '0';
  payload.admin_summary_enabled = adminSummaryEnabled.value ? '1' : '0';

  // independent third-party review before send
  payload.reviewer_enabled = reviewerEnabled.value ? '1' : '0';

  // formerly-hidden behaviour dials (settings-truthfulness 2026-07-12)
  payload.night_ack = nightAck.value ? '1' : '0';
  payload.verify_nudge = verifyNudge.value ? '1' : '0';
  payload.ack_sticker = ackSticker.value ? '1' : '0';
  payload.ack_sticker_id = ackStickerId.value.trim();
  payload.day_safe_classes = [...new Set(daySafeClasses.value)].join(',');
  payload.night_safe_promote = [...new Set(nightSafePromote.value)].join(',');

  // held-turn acknowledgement lane + the mh_keep_unreplied master it needs to send
  payload.keep_unreplied_enabled = keepUnrepliedEnabled.value ? '1' : '0';
  payload.needs_human_ack = needsHumanAck.value;
  payload.needs_human_ack_cooldown_min = String(
    clampCooldown(
      Number(needsHumanAckCooldownMin.value) || NEEDS_HUMAN_ACK_COOLDOWN_DEFAULT
    )
  );

  // accounts the AI must never reply to (phone / email / username)
  payload.exclude_accounts = excludeAccounts.value.join(',');

  // AI reply wait (sliding debounce window, seconds); 0 = reply immediately
  payload.debounce_seconds = String(
    Math.max(0, Math.min(600, Number(debounceSeconds.value) || 0))
  );

  return payload;
};

const loadConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await window.axios.get(baseUrl.value);
    hydrate(data);
  } catch (error) {
    useAlert(t('AI_AUTOPILOT.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await window.axios.put(baseUrl.value, buildPayload());
    hydrate(data);
    useAlert(t('AI_AUTOPILOT.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(t('AI_AUTOPILOT.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  store.dispatch('inboxes/get');
  loadConfig();
  clockTimer = setInterval(() => {
    nowTick.value = Date.now();
  }, 30000);
});

onBeforeUnmount(() => {
  if (clockTimer) clearInterval(clockTimer);
});
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader
      :title="$t('AI_AUTOPILOT.TITLE')"
      :description="$t('AI_AUTOPILOT.DESCRIPTION')"
    />

    <div class="flex-grow flex-shrink min-w-0 mt-3">
      <woot-loading-state v-if="isLoading" />

      <form v-else @submit.prevent="save">
        <!-- Live status chip (covers both bot + team state) -->
        <div
          class="inline-flex items-center gap-2 self-start rounded-full px-3 py-1.5 text-sm font-medium"
          :class="{
            'bg-n-teal-3 text-n-teal-11': statusChip.tone === 'on',
            'bg-n-slate-3 text-n-slate-11': statusChip.tone === 'sleeping',
            'bg-n-ruby-3 text-n-ruby-11': statusChip.tone === 'off',
          }"
        >
          {{ statusChip.label }}
        </div>

        <!-- Section 1: Working hours (team) -->
        <SectionLayout
          :title="$t('AI_AUTOPILOT.WORK.TITLE')"
          :description="$t('AI_AUTOPILOT.WORK.NOTE')"
          class="!pt-4"
        >
          <div class="grid gap-4">
            <fieldset class="grid gap-2">
              <legend class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('AI_AUTOPILOT.WORK.DAYS_LABEL') }}
              </legend>
              <div class="flex flex-wrap gap-2">
                <button
                  v-for="day in WEEK_DAYS"
                  :key="day.value"
                  type="button"
                  class="rounded-full border px-3 py-1.5 text-sm font-medium transition-colors"
                  :class="
                    workDays.includes(day.value)
                      ? 'border-n-teal-8 bg-n-teal-2 text-n-teal-11'
                      : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
                  "
                  @click="toggleWorkDay(day.value)"
                >
                  {{ $t(`AI_AUTOPILOT.WORK.DAY.${day.key}`) }}
                </button>
              </div>
            </fieldset>

            <div class="flex flex-wrap items-end gap-4">
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_AUTOPILOT.WORK.START') }}
                </span>
                <input v-model="workStart" type="time" class="!mb-0 text-sm" />
              </label>
              <label class="grid gap-1">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_AUTOPILOT.WORK.END') }}
                </span>
                <input v-model="workEnd" type="time" class="!mb-0 text-sm" />
              </label>
            </div>

            <p class="text-xs text-n-slate-10">
              {{ $t('AI_AUTOPILOT.WORK.HELP') }}
            </p>
          </div>
        </SectionLayout>

        <!-- Section 2: Bot hours (autonomous replies) -->
        <SectionLayout
          :title="$t('AI_AUTOPILOT.SECTION.TITLE')"
          :description="$t('AI_AUTOPILOT.SECTION.NOTE')"
          with-border
        >
          <div class="grid gap-6">
            <!-- Mode selector -->
            <fieldset class="grid gap-2">
              <legend class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('AI_AUTOPILOT.MODE.LABEL') }}
              </legend>
              <label
                v-for="option in [
                  { value: 'off', label: $t('AI_AUTOPILOT.MODE.OFF') },
                  {
                    value: 'scheduled',
                    label: $t('AI_AUTOPILOT.MODE.SCHEDULED'),
                  },
                  { value: 'always', label: $t('AI_AUTOPILOT.MODE.ALWAYS') },
                ]"
                :key="option.value"
                class="flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
                :class="
                  mode === option.value
                    ? 'border-n-teal-8 bg-n-teal-2'
                    : 'border-n-weak hover:bg-n-alpha-1'
                "
              >
                <input
                  v-model="mode"
                  type="radio"
                  :value="option.value"
                  class="accent-n-teal-9"
                />
                <span class="text-sm text-n-slate-12">{{ option.label }}</span>
              </label>
            </fieldset>

            <!-- Scheduled time pickers -->
            <div
              v-if="mode === 'scheduled'"
              class="grid gap-3 rounded-lg border border-n-weak p-4"
            >
              <div class="flex flex-wrap items-end gap-4">
                <label class="grid gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ $t('AI_AUTOPILOT.SCHEDULE.START') }}
                  </span>
                  <input
                    v-model="nightStart"
                    type="time"
                    class="!mb-0 text-sm"
                  />
                </label>
                <label class="grid gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ $t('AI_AUTOPILOT.SCHEDULE.END') }}
                  </span>
                  <input v-model="nightEnd" type="time" class="!mb-0 text-sm" />
                </label>
              </div>
              <p class="text-xs text-n-slate-10">
                {{ $t('AI_AUTOPILOT.SCHEDULE.OVERNIGHT_HELP') }}
              </p>
            </div>

            <!-- Channels -->
            <fieldset v-if="mode !== 'off'" class="grid gap-2">
              <legend class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('AI_AUTOPILOT.CHANNELS.LABEL') }}
              </legend>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="option in autoSendChannelOptions"
                  :key="option.token"
                  class="flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors"
                  :class="
                    selectedChannels.includes(option.token)
                      ? 'border-n-teal-8 bg-n-teal-2'
                      : 'border-n-weak hover:bg-n-alpha-1'
                  "
                >
                  <input
                    type="checkbox"
                    :checked="selectedChannels.includes(option.token)"
                    class="accent-n-teal-9"
                    @change="toggleChannel(option.token)"
                  />
                  <span class="text-sm text-n-slate-12">
                    {{ option.label }}
                  </span>
                </label>
              </div>
            </fieldset>

            <!-- Info note -->
            <p
              class="rounded-lg bg-n-alpha-1 px-3 py-2.5 text-xs leading-relaxed text-n-slate-11"
            >
              {{ $t('AI_AUTOPILOT.INFO_NOTE') }}
            </p>
          </div>
        </SectionLayout>

        <!-- Section: Autonomous send behaviour (formerly-hidden dials) -->
        <SectionLayout
          :title="$t('AI_AUTOPILOT.BEHAVIOUR.TITLE')"
          :description="$t('AI_AUTOPILOT.BEHAVIOUR.NOTE')"
          with-border
        >
          <div class="grid gap-4">
            <!-- Effective-status banner: what the AI is doing right now -->
            <div
              class="rounded-lg px-3 py-2.5 text-sm font-medium"
              :class="{
                'bg-n-teal-3 text-n-teal-11': effectiveStatus.tone === 'on',
                'bg-n-slate-3 text-n-slate-11':
                  effectiveStatus.tone === 'draft',
                'bg-n-ruby-3 text-n-ruby-11': effectiveStatus.tone === 'off',
              }"
            >
              {{ effectiveStatus.label }}
            </div>

            <!-- Night acknowledgement reply -->
            <label
              class="flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
              :class="
                nightAck
                  ? 'border-n-teal-8 bg-n-teal-2'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
            >
              <input
                v-model="nightAck"
                type="checkbox"
                class="mt-0.5 accent-n-teal-9"
              />
              <span class="grid gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AI_AUTOPILOT.BEHAVIOUR.NIGHT_ACK_LABEL') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('AI_AUTOPILOT.BEHAVIOUR.NIGHT_ACK_HELP') }}
                </span>
              </span>
            </label>

            <!-- Keep held conversations unreplied (mh_keep_unreplied master) -->
            <label
              class="flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
              :class="
                keepUnrepliedEnabled
                  ? 'border-n-teal-8 bg-n-teal-2'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
            >
              <input
                v-model="keepUnrepliedEnabled"
                type="checkbox"
                class="mt-0.5 accent-n-teal-9"
              />
              <span class="grid gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AI_AUTOPILOT.BEHAVIOUR.KEEP_UNREPLIED_LABEL') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('AI_AUTOPILOT.BEHAVIOUR.KEEP_UNREPLIED_HELP') }}
                </span>
              </span>
            </label>

            <!-- Held-turn acknowledgement lane: off / author-only / send -->
            <fieldset class="grid gap-2">
              <legend class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.NEEDS_HUMAN_ACK_LABEL') }}
              </legend>
              <label
                v-for="state in NEEDS_HUMAN_ACK_OPTIONS"
                :key="state"
                class="flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
                :class="
                  needsHumanAck === state
                    ? 'border-n-teal-8 bg-n-teal-2'
                    : 'border-n-weak hover:bg-n-alpha-1'
                "
              >
                <input
                  v-model="needsHumanAck"
                  type="radio"
                  :value="state"
                  class="accent-n-teal-9"
                />
                <span class="text-sm text-n-slate-12">
                  {{
                    $t(
                      `AI_AUTOPILOT.BEHAVIOUR.NEEDS_HUMAN_ACK_OPTION.${state.toUpperCase()}`
                    )
                  }}
                </span>
              </label>
              <p class="text-xs text-n-slate-10">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.NEEDS_HUMAN_ACK_HELP') }}
              </p>
              <p
                v-if="needsHumanAck === 'on' && !keepUnrepliedEnabled"
                class="text-xs text-n-ruby-11"
              >
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.NEEDS_HUMAN_ACK_INTERLOCK') }}
              </p>
            </fieldset>

            <!-- Per-conversation burst floor for that acknowledgement -->
            <label v-if="needsHumanAck !== 'off'" class="grid gap-1 max-w-xs">
              <span class="text-xs font-medium text-n-slate-11">
                {{
                  $t('AI_AUTOPILOT.BEHAVIOUR.NEEDS_HUMAN_ACK_COOLDOWN_LABEL')
                }}
              </span>
              <input
                v-model.number="needsHumanAckCooldownMin"
                type="number"
                :min="NEEDS_HUMAN_ACK_COOLDOWN_MIN"
                :max="NEEDS_HUMAN_ACK_COOLDOWN_MAX"
                step="15"
                class="!mb-0 text-sm"
              />
              <span class="text-xs text-n-slate-10">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.NEEDS_HUMAN_ACK_COOLDOWN_HELP') }}
              </span>
            </label>

            <!-- Second verify pass -->
            <label
              class="flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
              :class="
                verifyNudge
                  ? 'border-n-teal-8 bg-n-teal-2'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
            >
              <input
                v-model="verifyNudge"
                type="checkbox"
                class="mt-0.5 accent-n-teal-9"
              />
              <span class="grid gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AI_AUTOPILOT.BEHAVIOUR.VERIFY_NUDGE_LABEL') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('AI_AUTOPILOT.BEHAVIOUR.VERIFY_NUDGE_HELP') }}
                </span>
              </span>
            </label>

            <!-- Acknowledgement sticker (dual behaviour: social + Shopee CRR) -->
            <label
              class="flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
              :class="
                ackSticker
                  ? 'border-n-teal-8 bg-n-teal-2'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
            >
              <input
                v-model="ackSticker"
                type="checkbox"
                class="mt-0.5 accent-n-teal-9"
              />
              <span class="grid gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AI_AUTOPILOT.BEHAVIOUR.ACK_STICKER_LABEL') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('AI_AUTOPILOT.BEHAVIOUR.ACK_STICKER_HELP') }}
                </span>
              </span>
            </label>

            <!-- Sticker id (only meaningful when the sticker is on) -->
            <label v-if="ackSticker" class="grid gap-1 max-w-xs">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.ACK_STICKER_ID_LABEL') }}
              </span>
              <input
                v-model="ackStickerId"
                type="text"
                :placeholder="
                  $t('AI_AUTOPILOT.BEHAVIOUR.ACK_STICKER_ID_PLACEHOLDER')
                "
                class="!mb-0 text-sm"
              />
              <span class="text-xs text-n-slate-10">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.ACK_STICKER_ID_HELP') }}
              </span>
            </label>

            <!-- Daytime auto-send classes -->
            <fieldset class="grid gap-2">
              <legend class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.DAY_SAFE_LABEL') }}
              </legend>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="token in DAY_SAFE_OPTIONS"
                  :key="token"
                  class="flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors"
                  :class="
                    daySafeClasses.includes(token)
                      ? 'border-n-teal-8 bg-n-teal-2'
                      : 'border-n-weak hover:bg-n-alpha-1'
                  "
                >
                  <input
                    type="checkbox"
                    :checked="daySafeClasses.includes(token)"
                    class="accent-n-teal-9"
                    @change="toggleDaySafe(token)"
                  />
                  <span class="text-sm text-n-slate-12">
                    {{
                      $t(
                        `AI_AUTOPILOT.BEHAVIOUR.DAY_SAFE_OPTION.${token.toUpperCase()}`
                      )
                    }}
                  </span>
                </label>
              </div>
              <p class="text-xs text-n-slate-10">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.DAY_SAFE_HELP') }}
              </p>
            </fieldset>

            <!-- Night defer promotion -->
            <fieldset class="grid gap-2">
              <legend class="text-sm font-medium text-n-slate-12 mb-1">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.NIGHT_PROMOTE_LABEL') }}
              </legend>
              <div class="flex flex-wrap gap-2">
                <label
                  v-for="token in NIGHT_PROMOTE_OPTIONS"
                  :key="token"
                  class="flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors"
                  :class="
                    nightSafePromote.includes(token)
                      ? 'border-n-teal-8 bg-n-teal-2'
                      : 'border-n-weak hover:bg-n-alpha-1'
                  "
                >
                  <input
                    type="checkbox"
                    :checked="nightSafePromote.includes(token)"
                    class="accent-n-teal-9"
                    @change="toggleNightPromote(token)"
                  />
                  <span class="text-sm text-n-slate-12">
                    {{
                      $t(
                        `AI_AUTOPILOT.BEHAVIOUR.NIGHT_PROMOTE_OPTION.${token.toUpperCase()}`
                      )
                    }}
                  </span>
                </label>
              </div>
              <p class="text-xs text-n-slate-10">
                {{ $t('AI_AUTOPILOT.BEHAVIOUR.NIGHT_PROMOTE_HELP') }}
              </p>
            </fieldset>
          </div>
        </SectionLayout>

        <!-- Section: AI draft per channel (independent of the autonomous-reply hours above) -->
        <SectionLayout
          :title="$t('AI_AUTOPILOT.DRAFT.TITLE')"
          :description="$t('AI_AUTOPILOT.DRAFT.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <div class="flex flex-wrap gap-2">
              <label
                v-for="option in channelOptions"
                :key="option.token"
                class="flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors"
                :class="
                  isDraftOn(option.token)
                    ? 'border-n-teal-8 bg-n-teal-2'
                    : 'border-n-ruby-8 bg-n-ruby-2'
                "
              >
                <input
                  type="checkbox"
                  :checked="isDraftOn(option.token)"
                  class="accent-n-teal-9"
                  @change="toggleDraftChannel(option.token)"
                />
                <span class="text-sm text-n-slate-12">
                  {{ option.label }}
                </span>
                <span
                  class="text-xs font-medium"
                  :class="
                    isDraftOn(option.token)
                      ? 'text-n-teal-11'
                      : 'text-n-ruby-11'
                  "
                >
                  {{
                    isDraftOn(option.token)
                      ? $t('AI_AUTOPILOT.DRAFT.ON')
                      : $t('AI_AUTOPILOT.DRAFT.OFF')
                  }}
                </span>
              </label>
            </div>
            <p class="text-xs text-n-slate-10">
              {{ $t('AI_AUTOPILOT.DRAFT.HELP') }}
            </p>
          </div>
        </SectionLayout>

        <!-- Section 3: Instant delivery staff alerts -->
        <SectionLayout
          :title="$t('AI_AUTOPILOT.INSTANT.TITLE')"
          :description="$t('AI_AUTOPILOT.INSTANT.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <label
              class="flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
              :class="
                instantOrderEnabled
                  ? 'border-n-teal-8 bg-n-teal-2'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
            >
              <input
                v-model="instantOrderEnabled"
                type="checkbox"
                class="mt-0.5 accent-n-teal-9"
              />
              <span class="grid gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AI_AUTOPILOT.INSTANT.ORDER_LABEL') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('AI_AUTOPILOT.INSTANT.ORDER_HELP') }}
                </span>
              </span>
            </label>

            <label
              class="flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
              :class="
                instantSummaryEnabled
                  ? 'border-n-teal-8 bg-n-teal-2'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
            >
              <input
                v-model="instantSummaryEnabled"
                type="checkbox"
                class="mt-0.5 accent-n-teal-9"
              />
              <span class="grid gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AI_AUTOPILOT.INSTANT.SUMMARY_LABEL') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('AI_AUTOPILOT.INSTANT.SUMMARY_HELP') }}
                </span>
              </span>
            </label>

            <label
              class="flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
              :class="
                adminSummaryEnabled
                  ? 'border-n-teal-8 bg-n-teal-2'
                  : 'border-n-weak hover:bg-n-alpha-1'
              "
            >
              <input
                v-model="adminSummaryEnabled"
                type="checkbox"
                class="mt-0.5 accent-n-teal-9"
              />
              <span class="grid gap-0.5">
                <span class="text-sm font-medium text-n-slate-12">
                  {{ $t('AI_AUTOPILOT.INSTANT.ADMIN_SUMMARY_LABEL') }}
                </span>
                <span class="text-xs text-n-slate-10">
                  {{ $t('AI_AUTOPILOT.INSTANT.ADMIN_SUMMARY_HELP') }}
                </span>
              </span>
            </label>
          </div>
        </SectionLayout>

        <!-- Section 4: Excluded accounts (AI never replies to these) -->
        <SectionLayout
          :title="$t('AI_AUTOPILOT.EXCLUDE.TITLE')"
          :description="$t('AI_AUTOPILOT.EXCLUDE.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <div class="flex flex-wrap items-end gap-2">
              <label class="grid gap-1 flex-grow">
                <span class="text-xs font-medium text-n-slate-11">
                  {{ $t('AI_AUTOPILOT.EXCLUDE.LABEL') }}
                </span>
                <input
                  v-model="newExclude"
                  type="text"
                  :placeholder="$t('AI_AUTOPILOT.EXCLUDE.PLACEHOLDER')"
                  class="!mb-0 text-sm"
                  @keydown.enter.prevent="addExclude"
                />
              </label>
              <button
                type="button"
                class="rounded-lg border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-1"
                @click="addExclude"
              >
                {{ $t('AI_AUTOPILOT.EXCLUDE.ADD') }}
              </button>
            </div>

            <div v-if="excludeAccounts.length" class="flex flex-wrap gap-2">
              <span
                v-for="item in excludeAccounts"
                :key="item"
                class="inline-flex items-center gap-1.5 rounded-full bg-n-slate-3 px-3 py-1 text-sm text-n-slate-12"
              >
                {{ item }}
                <button
                  type="button"
                  class="text-base leading-none text-n-slate-10 hover:text-n-ruby-11"
                  :aria-label="$t('AI_AUTOPILOT.EXCLUDE.REMOVE')"
                  @click="removeExclude(item)"
                >
                  {{ '\u2715' }}
                </button>
              </span>
            </div>
            <p v-else class="text-xs text-n-slate-10">
              {{ $t('AI_AUTOPILOT.EXCLUDE.EMPTY') }}
            </p>

            <p class="text-xs text-n-slate-10">
              {{ $t('AI_AUTOPILOT.EXCLUDE.HELP') }}
            </p>
          </div>
        </SectionLayout>

        <!-- Section 5: AI reply wait (sliding debounce window) -->
        <SectionLayout
          :title="$t('AI_AUTOPILOT.DEBOUNCE.TITLE')"
          :description="$t('AI_AUTOPILOT.DEBOUNCE.NOTE')"
          with-border
        >
          <div class="grid gap-3">
            <label class="grid gap-1 max-w-xs">
              <span class="text-xs font-medium text-n-slate-11">
                {{ $t('AI_AUTOPILOT.DEBOUNCE.LABEL') }}
              </span>
              <input
                v-model.number="debounceSeconds"
                type="number"
                min="0"
                max="600"
                step="5"
                class="!mb-0 text-sm"
              />
            </label>
            <p class="text-xs text-n-slate-10">
              {{ $t('AI_AUTOPILOT.DEBOUNCE.HELP') }}
            </p>
          </div>
        </SectionLayout>

        <!-- Section 6: Third-party review before send (independent AI CS reviewer) -->
        <SectionLayout
          :title="$t('AI_AUTOPILOT.REVIEW.TITLE')"
          :description="$t('AI_AUTOPILOT.REVIEW.NOTE')"
          with-border
        >
          <label
            class="flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors"
            :class="
              reviewerEnabled
                ? 'border-n-teal-8 bg-n-teal-2'
                : 'border-n-weak hover:bg-n-alpha-1'
            "
          >
            <input
              v-model="reviewerEnabled"
              type="checkbox"
              class="mt-0.5 accent-n-teal-9"
            />
            <span class="grid gap-0.5">
              <span class="text-sm font-medium text-n-slate-12">
                {{ $t('AI_AUTOPILOT.REVIEW.LABEL') }}
              </span>
              <span class="text-xs text-n-slate-10">
                {{ $t('AI_AUTOPILOT.REVIEW.HELP') }}
              </span>
            </span>
          </label>
        </SectionLayout>

        <div class="pt-2">
          <NextButton blue :is-loading="isSaving" type="submit">
            {{ $t('AI_AUTOPILOT.SAVE') }}
          </NextButton>
        </div>
      </form>
    </div>
  </div>
</template>
